import { ReactNode, useState } from 'react';
import { useFormState, type UseFormReturn } from 'react-hook-form';
import { Button, Group, MultiSelect, Select as MantineSelect, Stack } from '@mantine/core';
import styled from 'styled-components';

import { Select, Textarea, TextInput } from 'react-hook-form-mantine';
import {
	DocumentWorkflowTemplateSourceFormat,
	DocumentWorkflowTemplateType,
	DocumentWorkflowTemplateVisibleIn,
	SignatureType,
} from '@dealstack/shared/lib/generated';
import {
	allowsFieldPersonalisation,
	allowsManualSignaturePlacement,
} from '@dealstack/shared/lib/common/signatures';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Tooltip } from '@dealstack/frontend/shared/components/Tooltip/Tooltip';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { formatEnumValue } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/utils/formatEnumValue.ts';
import { getVisibleInOptions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/utils/getVisibleInOptions.ts';
import { getSignatureType } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/utils/getSignatureType.ts';
import { scheduleCouplingDisabled } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/utils/scheduleCouplingDisabled.ts';
import { useContractOptions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/hooks/useContractOptions.ts';
import { DocumentEditorModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/DocumentEditorModal/DocumentEditorModal.tsx';
import type { PanelHeaderEdit } from '@dealstack/frontend/shared/components/FieldBinding/TemplateNameField/templateNameField.types.ts';
import { CounterpartyNamesInput } from './CounterpartyNamesInput.tsx';
import { useMasterDocumentsScope } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/MasterDocumentsScopeContext.tsx';
import { DealFieldBindingEditor } from './DealFieldBindingEditor.tsx';
import { DealPdfDocumentEditor } from './DealPdfDocumentEditor.tsx';
import { MasterDocumentNameField } from './MasterDocumentNameField.tsx';
import { getFieldVisibility } from './useFieldVisibility.ts';
import {
	MASTER_DOCUMENT_FORM_FIELD,
	MasterDocumentFormDto,
	MasterDocumentFormMode,
} from './types.ts';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import type { TemplateSettingLocksRes } from '@dealstack/shared/lib/generated';

interface Props {
	form: UseFormReturn<MasterDocumentFormDto>;
	mode: MasterDocumentFormMode;
	classifierOptions: { value: string; label: string }[];
	disabled?: boolean;
	headerSlot?: ReactNode;
	footerSlot?: ReactNode;
	templateId?: string;
	templateFile?: Blob;
	templateSourceFormat?: DocumentWorkflowTemplateSourceFormat;
	/** Per-setting lock from the read. Locked once a document issued from this master document
	 *  carries a signature — changing these then breaks a signature already given. */
	settingLocks?: TemplateSettingLocksRes;
	/** Saves `requiresPostDating` immediately. Absent, the switch is a plain form edit. */
	onTogglePostDating?: (next: boolean) => void;
	/** True while {@link onTogglePostDating} is in flight. */
	isTogglingPostDating?: boolean;
	/** Submits this form. Given by the host that owns {@link footerSlot}'s Save, so the editor
	 *  modal's own Save can write a rename made in it rather than closing over the top of one.
	 *  Rejects when the write did not happen. */
	onSaveDocument?: () => Promise<void>;
	/** True while {@link onSaveDocument} is in flight. */
	isSavingDocument?: boolean;
}

const NarrowTooltip = styled(Tooltip)`
	& .react-tooltip {
		max-width: 16rem;
		word-break: break-word;
	}
`;

export const MasterDocumentForm = ({
	form,
	mode,
	classifierOptions,
	disabled = false,
	headerSlot,
	footerSlot,
	templateId,
	templateFile,
	templateSourceFormat,
	settingLocks,
	onTogglePostDating,
	isTogglingPostDating = false,
	onSaveDocument,
	isSavingDocument = false,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const INDIVIDUAL_SCHEDULE_TOOLTIP = tMipManage('master_documents.tooltip.individual_schedule');
	const INDIVIDUAL_COPIES_TOOLTIP = tMipManage('master_documents.tooltip.individual_copies');
	const MULTI_SIGN_EDITOR_TOOLTIP = tMipManage('master_documents.tooltip.editor_multi_sign', {
		defaultValue:
			'Every signer shares one copy of this document, so its fields cannot be personalised and its signatures cannot be placed. Turn on "Individual copies" to set it up.',
	});
	const INDIVIDUAL_SCHEDULE_COUPLING_TOOLTIP = tMipManage(
		'master_documents.tooltip.individual_couple'
	);

	const { signatureTypeOptions } = useContractOptions(tMipManage);
	/**
	 * A Word template places its signatures somewhere else, so it gets no Signatures tab here.
	 *
	 * This surface is a coordinate canvas over the uploaded file. A reflow document has no fixed
	 * file to draw on — its anchors live in the document itself, on the Word editor's own
	 * Signatures tab — so the two are different surfaces for the same job, not one shared with a
	 * format switch.
	 */
	const isWordTemplate = templateSourceFormat === DocumentWorkflowTemplateSourceFormat.DOCX;

	const selectedVisibleIn = form.watch(MASTER_DOCUMENT_FORM_FIELD.visibleIn) ?? [];
	const visibleInOptions = getVisibleInOptions(selectedVisibleIn);
	const isCreate = mode === 'create';
	const visibleInError = form.formState.errors.visibleIn?.message;
	const masterDocumentTypeOptions = Object.values(DocumentWorkflowTemplateType).map((t) => ({
		value: t,
		label: formatEnumValue(t),
	}));

	const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);

	// The editor opens over this form, which still owns `name`. It edits the SAME field rather
	// than saving on its own, so the form's Save stays the single writer — a direct write from
	// the modal would be overwritten by the stale value this form still holds.
	const documentNameField = (
		<MasterDocumentNameField control={form.control} disabled={disabled ?? false} />
	);
	// ...which is why the editor's Save submits THIS form rather than writing the name itself.
	// Without it the button under the rename saves the bindings, closes, and leaves the rename
	// behind, looking for all the world like it was saved.
	// `name`-scoped so a keystroke that changes nothing else does not re-render the form.
	const { dirtyFields } = useFormState({
		control: form.control,
		name: MASTER_DOCUMENT_FORM_FIELD.name,
	});
	const documentNameEdit: PanelHeaderEdit = {
		isDirty: !!dirtyFields.name,
		save: () => onSaveDocument?.(),
		discard: () => form.resetField(MASTER_DOCUMENT_FORM_FIELD.name),
		isSaving: isSavingDocument,
	};

	const documentType = form.watch(MASTER_DOCUMENT_FORM_FIELD.type);
	const signatureTypeValue = form.watch(MASTER_DOCUMENT_FORM_FIELD.signatureType);
	const individualInstances = form.watch(MASTER_DOCUMENT_FORM_FIELD.individualInstances);
	const scheduleFormValue =
		form.watch(MASTER_DOCUMENT_FORM_FIELD.individualScheduleRequired) ?? false;
	const visibility = getFieldVisibility({
		type: documentType,
		signatureType: signatureTypeValue,
		individualInstances: individualInstances ?? true,
	});
	// Wet ink never has witness signers, so a per-counterparty witness has nothing to sign.
	const counterpartyWitnessAvailable = signatureTypeValue !== SignatureType.WET_INK;

	const { organizationId } = useMasterDocumentsScope();

	// Slots to place come from electronic signing roles on a per-participant document.
	// Anything else — review-only, wet ink, or one instance shared by every signer — has no
	// slots this document could ever place, so the control is hidden rather than shown
	// disabled: it is not a setting this document could want.
	const canPlaceSignaturesManually = allowsManualSignaturePlacement({
		signatureType: getSignatureType({
			type: documentType,
			formSignatureType: signatureTypeValue,
		}),
		isMultiSign: individualInstances === false,
	});

	/**
	 * Bound fields resolve against ONE investor, and a shared instance is generated once per
	 * investor over the same bytes — so the values every signer would receive are whichever
	 * investor rendered last. Hidden rather than shown disabled, for the same reason as the
	 * Signatures tab above: it is not a job this document could ever have.
	 */
	const canPersonaliseFields = allowsFieldPersonalisation({
		isMultiSign: individualInstances === false,
	});

	const postDatingEnabled = form.watch(MASTER_DOCUMENT_FORM_FIELD.requiresPostDating) ?? false;

	/** Routed through `blockedReason` rather than disabling the button, which also opens the
	 *  fields tab. */
	const placementLocked =
		!!settingLocks?.useManualSignaturePlacement.locked ||
		!!settingLocks?.signaturePlacements.locked;
	const manualPlacementBlockedReason = placementLocked
		? tMipManage('master_documents.manual_placement.locked_by_signatures')
		: undefined;

	/**
	 * Placeable slots are the whole condition — there is no toggle to switch manual placement on
	 * first any more, so whether the document USES it is derived from whether the author placed
	 * anything, as the Word and KYC editors already do. Post-dating alone also earns the tab
	 * (DC-1000): the date boxes are placed on the same canvas.
	 */
	const showSignaturesTab = !isWordTemplate && (canPlaceSignaturesManually || postDatingEnabled);

	// With neither job the editor is an empty screen, so the button that opens it says why
	// instead. The Word editor is the document itself and always has prose to edit.
	const editorHasWork = isWordTemplate || canPersonaliseFields || showSignaturesTab;
	const openDocumentEditorButton = (
		<Button
			variant="outline"
			onClick={() => setIsEditorModalOpen(true)}
			disabled={disabled || !editorHasWork}
			data-testid={TEST_ELEMENT_IDS.MASTER_DOCUMENT_OPEN_DOCUMENT_EDITOR_BUTTON}
		>
			{tMipManage('master_documents.field_binding.open_editor')}
		</Button>
	);

	const isScheduleCouplingDisabled = scheduleCouplingDisabled({ individualInstances });
	const displayedScheduleChecked = isScheduleCouplingDisabled ? false : scheduleFormValue;
	const isEditMode = mode === 'edit';
	const typeSelectDisabled = disabled || isEditMode;
	const typeChangeBlockedReason = isEditMode
		? tMipManage('master_documents.tooltip.type_locked_after_creation')
		: undefined;

	return (
		<Stack gap="md" data-testid={`master-document-form-${mode}`}>
			{headerSlot}
			<TextInput
				name={MASTER_DOCUMENT_FORM_FIELD.name}
				control={form.control}
				label={tMipManage('master_documents.text.document_name')}
				size="md"
				placeholder={tMipManage('master_documents.text.name_placeholder')}
				disabled={disabled}
			/>
			{isEditMode && typeChangeBlockedReason ? (
				<NarrowTooltip tooltip={typeChangeBlockedReason} hoverChildrenInsteadIcon>
					<Select
						name={MASTER_DOCUMENT_FORM_FIELD.type}
						control={form.control}
						label={tMipManage('master_documents.text.document_type')}
						size="md"
						rightSection={<Icon variant="caret-down" size="lg" />}
						rightSectionProps={{ style: { pointerEvents: 'none' } }}
						data={masterDocumentTypeOptions}
						disabled={typeSelectDisabled}
					/>
				</NarrowTooltip>
			) : (
				<Select
					name={MASTER_DOCUMENT_FORM_FIELD.type}
					control={form.control}
					label={tMipManage('master_documents.text.document_type')}
					size="md"
					rightSection={<Icon variant="caret-down" size="lg" />}
					rightSectionProps={{ style: { pointerEvents: 'none' } }}
					data={masterDocumentTypeOptions}
					disabled={typeSelectDisabled}
				/>
			)}
			{!organizationId && (
				<MultiSelect
					data={classifierOptions}
					label={tMipManage('master_documents.text.classification')}
					placeholder={tMipManage('master_documents.text.classification_placeholder')}
					value={form.watch(MASTER_DOCUMENT_FORM_FIELD.classifiers) ?? []}
					onChange={(value: string[]) => {
						form.setValue(MASTER_DOCUMENT_FORM_FIELD.classifiers, value, {
							shouldDirty: true,
						});
					}}
					rightSection={<Icon variant="caret-down" size="lg" />}
					styles={{ rightSection: { pointerEvents: 'none' } }}
					size="md"
					clearable
					disabled={disabled}
				/>
			)}
			<MultiSelect
				data={visibleInOptions}
				label={tMipManage('master_documents.text.applicable_journeys')}
				placeholder={tMipManage('master_documents.text.applicability_placeholder')}
				// Required on create only — see MasterDocumentCreateShape. The field is driven by
				// setValue rather than `control`, so the resolver's error has to be surfaced here
				// by hand or `handleSubmit` blocks with nothing on screen to explain why.
				withAsterisk={isCreate}
				error={isCreate ? visibleInError : undefined}
				value={selectedVisibleIn}
				onChange={(value: DocumentWorkflowTemplateVisibleIn[]) => {
					form.setValue(MASTER_DOCUMENT_FORM_FIELD.visibleIn, value, {
						shouldDirty: true,
						shouldValidate: true,
					});
				}}
				rightSection={<Icon variant="caret-down" size="lg" />}
				styles={{ rightSection: { pointerEvents: 'none' } }}
				size="md"
				clearable
				disabled={disabled}
			/>
			{visibility.signatureType.show &&
				(visibility.signatureType.locked && visibility.signatureType.lockedValue ? (
					<MantineSelect
						label={tMipManage('master_documents.text.signature_type')}
						size="md"
						rightSection={<Icon variant="caret-down" size="lg" />}
						rightSectionProps={{ style: { pointerEvents: 'none' } }}
						data={signatureTypeOptions}
						value={visibility.signatureType.lockedValue}
						disabled
						readOnly
					/>
				) : (
					<Select
						name={MASTER_DOCUMENT_FORM_FIELD.signatureType}
						control={form.control}
						label={tMipManage('master_documents.text.signature_type')}
						size="md"
						rightSection={<Icon variant="caret-down" size="lg" />}
						rightSectionProps={{ style: { pointerEvents: 'none' } }}
						data={signatureTypeOptions}
						disabled={disabled}
					/>
				))}
			{visibility.counterparties.show && (
				<CounterpartyNamesInput
					value={form.watch(MASTER_DOCUMENT_FORM_FIELD.counterparties) ?? []}
					onChange={(value) => {
						form.setValue(MASTER_DOCUMENT_FORM_FIELD.counterparties, value, {
							shouldDirty: true,
						});
					}}
					disabled={disabled}
					label={tMipManage('master_documents.text.counterparty_roles')}
					showWitnessCheckbox={isEditMode && counterpartyWitnessAvailable}
				/>
			)}
			{visibility.witnessRequired.show && (
				<AdvancedSwitch
					selectedValue={form.watch(MASTER_DOCUMENT_FORM_FIELD.witnessRequired)}
					onChange={(e) =>
						form.setValue(MASTER_DOCUMENT_FORM_FIELD.witnessRequired, !!e, {
							shouldDirty: true,
						})
					}
					rightText={
						isEditMode
							? tMipManage('master_documents.text.investor_witness_required')
							: tMipManage('master_documents.text.witness_required')
					}
					selectedColor="dark.8"
					unselectedColor="dark.8"
					unselectedFontWeight={600}
					unselectedLetterSpacing="0"
					disabled={disabled}
				/>
			)}
			{visibility.individualScheduleRequired.show && (
				<Group gap={6} align="center" wrap="nowrap">
					<AdvancedSwitch
						selectedValue={displayedScheduleChecked}
						onChange={(e) =>
							form.setValue(
								MASTER_DOCUMENT_FORM_FIELD.individualScheduleRequired,
								!!e,
								{ shouldDirty: true, shouldValidate: true }
							)
						}
						rightText={tMipManage('master_documents.text.individual_schedule')}
						selectedColor="dark.8"
						unselectedColor="dark.8"
						unselectedFontWeight={600}
						unselectedLetterSpacing="0"
						disabled={disabled || isScheduleCouplingDisabled}
					/>

					<NarrowTooltip
						tooltip={
							isScheduleCouplingDisabled
								? INDIVIDUAL_SCHEDULE_COUPLING_TOOLTIP
								: INDIVIDUAL_SCHEDULE_TOOLTIP
						}
						iconSize="md"
					/>
				</Group>
			)}
			{visibility.individualInstances.show && (
				<Group gap={6} align="center" wrap="nowrap">
					<AdvancedSwitch
						selectedValue={
							form.watch(MASTER_DOCUMENT_FORM_FIELD.individualInstances) ?? true
						}
						onChange={(e) =>
							form.setValue(MASTER_DOCUMENT_FORM_FIELD.individualInstances, !!e, {
								shouldDirty: true,
								shouldValidate: true,
							})
						}
						rightText={tMipManage('master_documents.text.individual_copies')}
						selectedColor="dark.8"
						unselectedColor="dark.8"
						unselectedFontWeight={600}
						unselectedLetterSpacing="0"
						disabled={disabled}
					/>

					<NarrowTooltip tooltip={INDIVIDUAL_COPIES_TOOLTIP} iconSize="md" />
				</Group>
			)}
			<Textarea
				name={MASTER_DOCUMENT_FORM_FIELD.instruction}
				control={form.control}
				label={tMipManage('master_documents.text.instructions_to_investors')}
				placeholder={tMipManage('master_documents.text.instruction_placeholder')}
				size="md"
				autosize
				minRows={4}
				disabled={disabled}
			/>

			{!organizationId && isEditMode && templateId && (
				<AdvancedSwitch
					rightText={tMipManage('master_documents.text.post_dating')}
					selectedValue={postDatingEnabled}
					onChange={(e) =>
						onTogglePostDating === undefined
							? form.setValue(MASTER_DOCUMENT_FORM_FIELD.requiresPostDating, !!e, {
									shouldDirty: true,
								})
							: onTogglePostDating(!!e)
					}
					disabled={disabled || isTogglingPostDating}
					selectedColor="dark.8"
					unselectedColor="dark.8"
					dataTestId={TEST_ELEMENT_IDS.MASTER_DOCUMENT_POST_DATING_SWITCH}
				/>
			)}

			{/* One editor, one button. Binding fields and placing signatures are both "set this
			    document up", and behind separate buttons the author had to close one to reach the
			    other. Each editor owns its own tabs; the modal is only the fullscreen box.

			    Programme-only: fields bind against deal data and placements belong to issued
			    instances, neither of which an organisation default has. */}
			{!organizationId && isEditMode && templateId && templateFile && (
				<>
					{editorHasWork ? (
						openDocumentEditorButton
					) : (
						<NarrowTooltip
							tooltip={MULTI_SIGN_EDITOR_TOOLTIP}
							hoverChildrenInsteadIcon
							dataTestId={
								TEST_ELEMENT_IDS.MASTER_DOCUMENT_DOCUMENT_EDITOR_BLOCKED_TOOLTIP
							}
						>
							{openDocumentEditorButton}
						</NarrowTooltip>
					)}
					<DocumentEditorModal
						opened={isEditorModalOpen}
						onClose={() => setIsEditorModalOpen(false)}
						title={tMipManage('master_documents.field_binding.modal_title')}
					>
						{isWordTemplate ? (
							// A reflow document is its own surface: the anchors live in the
							// content, so there is no coordinate canvas to share.
							<DealFieldBindingEditor
								templateId={templateId}
								file={templateFile}
								sourceFormat={templateSourceFormat}
								panelHeader={documentNameField}
								panelHeaderEdit={documentNameEdit}
								onClose={() => setIsEditorModalOpen(false)}
							/>
						) : (
							<DealPdfDocumentEditor
								templateId={templateId}
								file={templateFile}
								panelHeader={documentNameField}
								panelHeaderEdit={documentNameEdit}
								initialShowStampMetadata={
									form.getValues(MASTER_DOCUMENT_FORM_FIELD.showStampMetadata) ??
									true
								}
								// No toggle behind this any more: slots exist when the document
								// has electronic per-participant signing roles. Without them the
								// canvas is dates-only, which is the DC-1000 case.
								manualPlacementEnabled={canPlaceSignaturesManually}
								postDatingEnabled={postDatingEnabled}
								showFieldsTab={canPersonaliseFields}
								showSignaturesTab={showSignaturesTab}
								onClose={() => setIsEditorModalOpen(false)}
								{...(manualPlacementBlockedReason === undefined
									? {}
									: { blockedReason: manualPlacementBlockedReason })}
							/>
						)}
					</DocumentEditorModal>
				</>
			)}

			{footerSlot}
		</Stack>
	);
};
